import React from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import HistoryIcon from '@material-ui/icons/History';

const ORANGE = '#FF6900';

/**
 * "You have a program in progress" — continue it, or start again.
 *
 * Asked wherever someone can begin a program, because the builder RESUMES an
 * open chat session by default: without this question, "new program" silently
 * continues the previous one.
 *
 * Extracted because the same question was hand-written in four places
 * (DQProHome, DQProProducts, ImpactTab, the programs list), which is three
 * chances for the wording or the button order to drift. New callers should use
 * this; the existing three can move over when next touched.
 *
 * The tone is deliberate. The earlier version set the whole message in alarm
 * red, which reads as "something went wrong" — nothing has. This is an
 * ordinary fork with a safe default (Continue) and one destructive option
 * (Start Over, which deletes the draft), so only the consequence of the
 * destructive choice is coloured, and only faintly.
 *
 * Deliberately dumb: it knows nothing about how to find or clear a session.
 * The caller owns that, because the two differ — the list page looks the
 * active program up, ImpactTab already holds it.
 *
 * Props:
 *  open       – whether the dialog is showing
 *  onClose    – dismissed without choosing
 *  onContinue – keep the existing session
 *  onStartNew – discard it and begin again
 *  busy       – disables the actions while onStartNew is in flight
 */
const ExistingProgramDialog = ({ open, onClose, onContinue, onStartNew, busy = false }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
    >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
                aria-label="close"
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', right: 10, top: 10, color: 'var(--brand-text-muted, #9CA3AF)' }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ p: '32px 28px 24px', textAlign: 'center' }}>
                <Box sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(255,105,0,0.10)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                }}>
                    <HistoryIcon style={{ color: ORANGE, fontSize: 26 }} />
                </Box>

                <Typography sx={{
                    fontSize: '18px', fontWeight: 700, lineHeight: 1.35,
                    color: 'var(--brand-text, #1a1a1a)',
                }}>
                    You have a program in progress
                </Typography>

                <Typography sx={{
                    mt: 1, fontSize: '14px', lineHeight: 1.6,
                    color: 'var(--brand-text-muted, #6B7280)',
                }}>
                    Pick up where you left off, or start again from a blank conversation.
                    {' '}
                    {/* Only the destructive consequence is called out, and quietly. */}
                    <Box component="span" sx={{ color: '#C2410C' }}>
                        Starting over discards the current draft.
                    </Box>
                </Typography>
            </Box>

            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
                p: '16px 20px',
                borderTop: '1px solid var(--brand-border, #F0F0F0)',
                background: 'var(--brand-surface, #FCFCFD)',
            }}>
                <Button
                    onClick={onClose}
                    disabled={busy}
                    sx={{ textTransform: 'none', fontSize: '14px', color: 'var(--brand-text-muted, #6B7280)' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={onStartNew}
                    sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '10px',
                        px: 2,
                        color: 'var(--brand-text, #111)',
                        borderColor: 'var(--brand-border, #D1D5DB)',
                        '&:hover': { borderColor: 'var(--brand-text, #111)', background: 'transparent' },
                    }}
                >
                    {busy ? 'Starting…' : 'Start Over'}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={busy}
                    onClick={onContinue}
                    sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        px: 2.5,
                        backgroundColor: `${ORANGE} !important`,
                        '&:hover': { backgroundColor: '#E55E00 !important' },
                    }}
                >
                    Continue
                </Button>
            </Box>
        </DialogContent>
    </Dialog>
);

export default ExistingProgramDialog;
